import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import classNames from "classnames";
import format from "date-fns/format";
import type { FilterPreset } from "models/filterPreset";
import { twMerge } from "tailwind-merge";

import PresetsButton from "components/PresetsButton";
import { SwipperContext } from "components/Swipper";
import { baseFetcher } from "utils/baseFetcher";
import { INSIGHT_TYPES } from "consts/insightTypes";

import FilterDropdown from "../FilterDropDown";

const MoreFiltersModal = dynamic(() => import("./MoreFiltersModal"), {
  ssr: false,
});
const RegionsFilter = dynamic(() => import("./RegionsFilter"), { ssr: false });
const SectorsFilter = dynamic(() => import("./SectorsFilter"), { ssr: false });
const VerticalsFilter = dynamic(() => import("./VerticalsFilter"), {
  ssr: false,
});

const PresetsModal = dynamic(() => import("components/PresetsModal"));

export const filterPreset = "/api/v2/filter-presets";

export const TIME_HORIZON_MAP: { [key: string]: string } = {
  long: "Long (> 12 Months)",
  medium: "Medium (3 - 12 Months)",
  short: "Short (< 3 Months)",
};

export type InsightsListFiltersProps = {
  showMoreFilters?: boolean;
  showPresets?: boolean;
  showTopCountries?: boolean;
  overrideEndpoints?: {
    geographies?: string;
    countries?: string;
    sectors?: string;
    verticals?: string;
  };
  dropdownClassName?: string;
};

export const InsightsListFilters = ({
  showTopCountries,
  showMoreFilters,
  showPresets,
  overrideEndpoints,
  selected,
  applyPreset,
  onGeographiesChange,
  onCountriesChange,
  onSectorsChange,
  onVerticalsChange,
  onImperativesChange,
  onTimeHorizonsChange,
  onPublishedDatesChange,
  onInsightTypesChange,
  dropdownClassName,
}: InsightsListFiltersProps & {
  selected?: {
    geographies: string[];
    countries: string[];
    sectors: string[];
    verticals: string[];
    imperatives?: string[];
    timeHorizons?: string[];
    publishedDates?: { from?: Date; to?: Date };
    insightTypes?: string[];
  };
  applyPreset: (_preset: FilterPreset) => void;
  onGeographiesChange: (_geographies: any[]) => void;
  onCountriesChange: (_countries: any[]) => void;
  onSectorsChange: (_sectors: any[]) => void;
  onVerticalsChange: (_verticals: any[]) => void;
  onImperativesChange: (_imperatives: string[]) => void;
  onTimeHorizonsChange: (_timeHorizons: string[]) => void;
  onPublishedDatesChange: (_publishedDates: { from?: Date; to?: Date }) => void;
  onInsightTypesChange: (_insightTypes: string[]) => void;
}) => {
  const [showMoreFiltersModal, setShowMoreFiltersModal] = useState(false);
  const { setEnabledSwipe } = useContext(SwipperContext);

  const hasMoreFilters =
    selected?.imperatives?.length ||
    selected?.timeHorizons?.length ||
    selected?.publishedDates?.from ||
    selected?.publishedDates?.to ||
    selected?.insightTypes?.length;

  return (
    <div
      className={twMerge(
        "flex",
        "overflow-x-auto p-[15px] pb-[8px] md:p-0 shadow md:shadow-none"
      )}
      onScroll={() => {
        setEnabledSwipe?.(false);
      }}
    >
      <div className="flex flex-row-reverse md:flex-row gap-[15px]">
        <div className="flex space-x-[15px]">
          <FilterDropdown
            dropdown={
              <RegionsFilter
                dropdownClassName={dropdownClassName}
                overrideEndpoints={overrideEndpoints}
                selected={selected}
                onGeographiesChange={onGeographiesChange}
                onCountriesChange={onCountriesChange}
                showTopCountries={showTopCountries}
              />
            }
            selected={
              !!selected?.geographies?.length || !!selected?.countries?.length
            }
          >
            Regions
          </FilterDropdown>
          <FilterDropdown
            dropdown={
              <SectorsFilter
                dropdownClassName={dropdownClassName}
                overrideEndpoint={overrideEndpoints?.sectors}
                selected={selected}
                onSectorsChange={onSectorsChange}
              />
            }
            selected={!!selected?.sectors?.length}
          >
            Sectors
          </FilterDropdown>
          <FilterDropdown
            dropdown={
              <VerticalsFilter
                dropdownClassName={dropdownClassName}
                overrideEndpoint={overrideEndpoints?.verticals}
                selected={selected}
                onVerticalsChange={onVerticalsChange}
              />
            }
            selected={!!selected?.verticals?.length}
          >
            Verticals
          </FilterDropdown>
          {showMoreFilters && (
            <button
              className={classNames(
                hasMoreFilters
                  ? "bg-utah-sky text-white"
                  : "bg-[#ccc] text-active-black",
                "rounded-full border-none shadow px-[16px] cursor-pointer",
                "min-w-[110px] md:min-w-0",
                "text-sm md:text-base"
              )}
              onClick={() => setShowMoreFiltersModal(true)}
            >
              More Filters
            </button>
          )}
        </div>
        {showPresets && (
          <PresetsButton
            onApply={(preset) => {
              applyPreset(preset);
            }}
            className={
              "md:bg-inactive-gray md:bg-transparent text-active-black md:text-smart-teal-500 text-sm md:text-base rounded-full leading-[25px] min-w-[110px]"
            }
          />
        )}
      </div>
      {showMoreFiltersModal && (
        <MoreFiltersModal
          selected={selected}
          onClose={() => setShowMoreFiltersModal(false)}
          onImperativesChange={onImperativesChange}
          onTimeHorizonsChange={onTimeHorizonsChange}
          onPublishedDatesChange={onPublishedDatesChange}
          onInsightTypesChange={onInsightTypesChange}
        />
      )}
    </div>
  );
};

export const useInsightsFilters = ({
  useQuery = true,
  preSelectFromQuery,
  preset,
  preSelectedFilters,
  showPresets,
}: {
  useQuery?: boolean;
  preSelectFromQuery?: boolean;
  preset?: FilterPreset;
  preSelectedFilters?: {
    geographies?: string[];
    countries?: string[];
    sectors?: string[];
    verticals?: string[];
    imperatives?: string[];
    timeHorizons?: string[];
    publishedDates?: string[];
    insightTypes?: string[];
  };
  showPresets?: boolean;
} = {}) => {
  const router = useRouter();

  const queryFilters = useMemo(
    () => (router.query.filters as string)?.split("|") || [],
    [router.query.filters]
  );

  const selectedGeographies = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.geographies || [];

      return queryFilters
        .find((f) => f.startsWith("geographies:"))
        ?.replace("geographies:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedCountries = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.countries || [];

      return queryFilters
        .find((f) => f.startsWith("countries:"))
        ?.replace("countries:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedSectors = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.sectors || [];

      return queryFilters
        .find((f) => f.startsWith("sectors:"))
        ?.replace("sectors:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedVerticals = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.verticals || [];

      return queryFilters
        .find((f) => f.startsWith("verticals:"))
        ?.replace("verticals:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedImperatives = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.imperatives || [];

      return queryFilters
        .find((f) => f.startsWith("imperatives:"))
        ?.replace("imperatives:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedTimeHorizons = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.timeHorizons || [];

      return queryFilters
        .find((f) => f.startsWith("time-horizon:"))
        ?.replace("time-horizon:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedPublishedDates = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.publishedDates || [];

      return queryFilters
        .find((f) => f.startsWith("time-scope:"))
        ?.replace("time-scope:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectedInsightTypes = useMemo(
    () => {
      if (!useQuery && !preSelectFromQuery)
        return preSelectedFilters?.insightTypes || [];

      return queryFilters
        .find((f) => f.startsWith("insight-types:"))
        ?.replace("insight-types:", "")
        ?.split(",");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [appliedPreset, setAppliedPreset] = useState(preset);
  const [geographies, setGeographies] = useState<any[]>(
    selectedGeographies?.map?.((name) => ({ name })) || []
  );
  const [countries, setCountries] = useState<any[]>(
    selectedCountries?.map?.((name) => ({ name })) || []
  );
  const [sectors, setSectors] = useState<any[]>(
    selectedSectors?.map?.((name) => ({ name })) || []
  );
  const [verticals, setVerticals] = useState<any[]>(
    selectedVerticals?.map?.((id) => ({ id })) || []
  );
  const [imperatives, setImperatives] = useState<string[]>(
    selectedImperatives || []
  );
  const [timeHorizons, setTimeHorizons] = useState<string[]>(
    selectedTimeHorizons || []
  );
  const [publishedDates, setPublishedDates] = useState<{
    from?: Date;
    to?: Date;
  }>({
    from: selectedPublishedDates?.[0]
      ? new Date(selectedPublishedDates[0])
      : undefined,
    to: selectedPublishedDates?.[1]
      ? new Date(selectedPublishedDates[1])
      : undefined,
  });
  const [insightTypes, setInsightTypes] = useState<any[]>(
    selectedInsightTypes || []
  );

  const [showModal, toggleModal] = useState(false);

  useEffect(() => {
    baseFetcher("/api/v2/content-verticals").then((verticalsData) => {
      if (selectedVerticals?.length && verticalsData?.length) {
        setVerticals(
          selectedVerticals.map((id) =>
            verticalsData.find((vd: any) => vd.id === id)
          )
        );
      }
    });
  }, [selectedVerticals]);

  const onVerticalsChange = useCallback((verticals) => {
    if (verticals.length === 0 || verticals[0].name) {
      setVerticals(verticals);
    } else {
      baseFetcher("/api/v2/content-verticals").then((verticalsData) => {
        if (verticals?.length && verticalsData?.length) {
          setVerticals(
            verticals.map(({ id }: any) =>
              verticalsData.find((vd: any) => vd.id === id)
            )
          );
        }
      });
    }
  }, []);

  const selectedFilters = useMemo(
    () => ({
      geographies,
      countries,
      sectors,
      verticals,
      imperatives,
      timeHorizons,
      publishedDates,
      insightTypes,
    }),
    [
      countries,
      geographies,
      imperatives,
      publishedDates,
      sectors,
      timeHorizons,
      verticals,
      insightTypes,
    ]
  );

  const hasFilter = !!(
    geographies.length ||
    countries.length ||
    sectors.length ||
    verticals.length ||
    imperatives.length ||
    timeHorizons.length ||
    publishedDates?.from ||
    publishedDates?.to ||
    insightTypes.length
  );

  const applyPreset = useCallback(
    (preset: FilterPreset) => {
      setGeographies(
        preset.geographies?.map?.((name: string) => ({ name })) || []
      );
      setCountries(preset.countries?.map?.((name: string) => ({ name })) || []);
      setSectors(preset.sectors?.map?.((name: string) => ({ name })) || []);
      onVerticalsChange(
        preset.verticals?.map?.((id: string) => ({ id })) || []
      );
      setImperatives(preset.imperatives || []);
      setTimeHorizons(preset.timeHorizons || []);
      setPublishedDates({
        from: preset.timeScope?.[0]
          ? new Date(preset.timeScope?.[0])
          : undefined,
        to: preset.timeScope?.[1] ? new Date(preset.timeScope?.[1]) : undefined,
      });
      setAppliedPreset(preset);
      setInsightTypes(preset.excludedInsightTypes);
    },
    [onVerticalsChange]
  );

  const InsightsSelectedFilters = useMemo(
    () =>
      function InsightsSelectedFilters({ noPadding, noBorder }: any) {
        return (
          <div>
            {hasFilter && (
              <div
                className={twMerge(
                  "border-0",
                  noBorder ? "" : "border-b border-solid border-black/20"
                )}
              >
                <div className="text-right">
                  {showPresets && (
                    <div
                      className={twMerge(
                        "inline-block cursor-pointer mt-[12px]",
                        "text-[12px] text-smart-teal",
                        "px-[10px] py-[3px]",
                        "border border-solid border-smart-teal rounded-full"
                      )}
                      onClick={() => {
                        toggleModal(true);
                      }}
                    >
                      {appliedPreset ? "Edit Preset" : "Save as Preset"}
                    </div>
                  )}
                  <div
                    className={twMerge(
                      "inline-block cursor-pointer mt-[12px] mr-[20px]",
                      "text-[13px] text-smart-teal",
                      "px-[10px] py-[3px]"
                    )}
                    onClick={() => {
                      setGeographies([]);
                      setCountries([]);
                      setSectors([]);
                      setVerticals([]);
                      setImperatives([]);
                      setTimeHorizons([]);
                      setPublishedDates({});
                      setInsightTypes([]);
                      setAppliedPreset(undefined);
                    }}
                  >
                    Clear
                  </div>
                </div>
                <div
                  className={twMerge(
                    "overflow-scroll md:overflow-auto whitespace-nowrap md:whitespace-normal pb-0",
                    "flex md:flex-wrap",
                    "mx-[-5px]",
                    noPadding ? "" : "px-[15px] md:px-[30px] py-[15px]"
                  )}
                >
                  {INSIGHT_TYPES.filter((type) =>
                    insightTypes?.includes(type.key)
                  ).map((type) => {
                    return (
                      <div
                        key={type.key}
                        className={classNames(
                          "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                        )}
                      >
                        No {type.name}
                        <span
                          className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                          onClick={() => {
                            const newInsightTypes = insightTypes.filter(
                              (t) => t !== type.key
                            );
                            setInsightTypes(newInsightTypes);
                            setAppliedPreset(undefined);
                          }}
                        >
                          ×
                        </span>
                      </div>
                    );
                  })}
                  {geographies.map((geography: any) => (
                    <div
                      className={classNames(
                        "flex items-center justify-center rounded-full border-none bg-black/10 shadow text-[13px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                      key={geography.name}
                      onClick={() => {
                        setGeographies(
                          geographies.filter(
                            (g: any) => g.name !== geography.name
                          )
                        );
                        setAppliedPreset(undefined);
                      }}
                    >
                      {geography.name}
                      <span
                        className="cursor-pointer text-[1.5em] text-black/30 hover:text-red-500 ml-[5px]"
                        onClick={() => {
                          setGeographies(
                            sectors.filter(
                              (g: any) => g.name !== geography.name
                            )
                          );
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {countries.map((country: any) => (
                    <div
                      className={classNames(
                        "flex items-center justify-center rounded-full border-none bg-black/10 shadow text-[13px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                      key={country.name}
                      onClick={() => {
                        setCountries(
                          countries.filter((c: any) => c.id !== country.id)
                        );
                        setAppliedPreset(undefined);
                      }}
                    >
                      {country.name}
                      <span
                        className="cursor-pointer text-[1.5em] text-black/30 hover:text-red-500 ml-[5px]"
                        onClick={() => {
                          setCountries(
                            countries.filter(
                              (s: any) => s.name !== country.name
                            )
                          );
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {sectors.map((sector: any) => (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                      key={sector.name}
                      onClick={() => {
                        setSectors(
                          sectors.filter((s: any) => s.name !== sector.name)
                        );
                        setAppliedPreset(undefined);
                      }}
                    >
                      {sector.name}
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setSectors(
                            sectors.filter((s: any) => s.name !== sector.name)
                          );
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {verticals.map((vertical: any) => (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                      key={vertical.id}
                    >
                      {vertical.name}
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setVerticals(
                            verticals.filter((v: any) => v.id !== vertical.id)
                          );
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {imperatives.map((imperative) => (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                      key={imperative}
                    >
                      <span className="capitalize">{imperative}</span>
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setImperatives(
                            imperatives.filter((v: any) => v !== imperative)
                          );
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {timeHorizons.map((timeHorizon) => (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                      key={timeHorizon}
                    >
                      <span className="capitalize">
                        {TIME_HORIZON_MAP[timeHorizon]}
                      </span>
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setTimeHorizons(
                            timeHorizons.filter((v: any) => v !== timeHorizon)
                          );
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {publishedDates?.from && publishedDates?.to ? (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                    >
                      <span suppressHydrationWarning>
                        between {format(publishedDates?.from, "d MMM yyyy")} -{" "}
                        {format(publishedDates?.to, "d MMM yyyy")}
                      </span>
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setPublishedDates({});
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ) : publishedDates?.from ? (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                    >
                      <span suppressHydrationWarning>
                        since {format(publishedDates?.from, "d MMM yyyy")}
                      </span>
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setPublishedDates({});
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ) : publishedDates?.to ? (
                    <div
                      className={classNames(
                        "rounded-full border-none bg-black/10 shadow text-[13px] leading-[31px] px-[16px] mb-[10px] mx-[5px]"
                      )}
                    >
                      <span suppressHydrationWarning>
                        until {format(publishedDates?.to, "d MMM yyyy")}
                      </span>
                      <span
                        className="cursor-pointer text-[1.5em] leading-[31px] text-black/30 hover:text-red-500 ml-[5px] align-bottom"
                        onClick={() => {
                          setPublishedDates({});
                          setAppliedPreset(undefined);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            )}
            {showModal && (
              <PresetsModal
                onClose={() => toggleModal(false)}
                onApply={(preset) => {
                  toggleModal(false);
                  applyPreset(preset);
                }}
                preShowForm
                preSelectedPreset={appliedPreset}
              />
            )}
          </div>
        );
      },
    [
      appliedPreset,
      applyPreset,
      countries,
      geographies,
      hasFilter,
      imperatives,
      insightTypes,
      publishedDates?.from,
      publishedDates?.to,
      sectors,
      showModal,
      showPresets,
      timeHorizons,
      verticals,
    ]
  );

  useEffect(() => {
    if (!useQuery) return;

    const queryFilters = [];

    if (countries?.length) {
      queryFilters.push(`countries:${countries.map((c) => c.name).join(",")}`);
    }
    if (geographies?.length) {
      queryFilters.push(
        `geographies:${geographies.map((c) => c.name).join(",")}`
      );
    }
    if (imperatives?.length) {
      queryFilters.push(`imperatives:${imperatives.join(",")}`);
    }
    if (sectors?.length) {
      queryFilters.push(`sectors:${sectors.map((c) => c.name).join(",")}`);
    }
    if (timeHorizons?.length) {
      queryFilters.push(`time-horizon:${timeHorizons.join(",")}`);
    }
    if (insightTypes?.length) {
      queryFilters.push(`insight-types:${insightTypes.join(",")}`);
    }
    if (publishedDates?.from || publishedDates?.to) {
      queryFilters.push(
        `time-scope:${
          publishedDates?.from?.toISOString() || "2014-08-31T17:00:00.000Z"
        }${publishedDates?.to ? "," + publishedDates?.to?.toISOString() : ""}`
      );
    }
    if (verticals?.length) {
      queryFilters.push(`verticals:${verticals.map((c) => c.id).join(",")}`);
    }

    if ((router.query.filters || "") !== queryFilters.join("|")) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...Object.keys(router.query)
              .filter((k) => k !== "filters")
              .reduce((a, k) => ({ ...a, [k]: router.query[k] }), {}),
            ...(queryFilters?.length
              ? { filters: queryFilters.join("|") }
              : {}),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  }, [
    geographies,
    countries,
    sectors,
    verticals,
    imperatives,
    timeHorizons,
    publishedDates,
    router,
    insightTypes,
    useQuery,
  ]);

  return {
    InsightsSelectedFilters,
    selectedFilters,
    filtersProps: {
      selected: selectedFilters,
      onGeographiesChange: setGeographies,
      onCountriesChange: setCountries,
      onSectorsChange: setSectors,
      onVerticalsChange,
      onImperativesChange: setImperatives,
      onTimeHorizonsChange: setTimeHorizons,
      onPublishedDatesChange: setPublishedDates,
      onInsightTypesChange: setInsightTypes,
      applyPreset,
    },
  };
};
